import type {ReactElement} from 'react';
import type {CssVariableStyle} from '@AiChat/lib/ai-chat-style.types';
import {assertNever} from '@Utils/assert-never.util';
import type {AiChatChristmasSnowflake} from './ai-chat-christmas-snowfall.types';
import {AI_CHAT_CHRISTMAS_SNOWFLAKES, AiChatChristmasSnowflakeShape} from './ai-chat-christmas-snowfall.types';
import styles from './ai-chat-christmas-snowfall.module.scss';

export function AiChatChristmasSnowfall(): ReactElement {
  const getFlakeColumn = (flake: AiChatChristmasSnowflake, index: number): ReactElement => {
    return (
      <span key={index} className={styles.flakeColumn} style={getFlakeColumnStyle(flake)}>
        <span className={styles.flake}>{getFlakeShape(flake.shape)}</span>
      </span>
    );
  };

  const getFlakeColumnStyle = (flake: AiChatChristmasSnowflake): CssVariableStyle => {
    return {
      '--fall-start': flake.startPercent,
      animationDelay: `-${flake.delaySeconds}s`,
      animationDuration: `${flake.fallSeconds}s`,
      left: `${flake.leftPercent}%`,
      width: `${flake.sizePx}px`,
    };
  };

  const getFlakeShape = (shape: AiChatChristmasSnowflakeShape): ReactElement => {
    switch (shape) {
      case AiChatChristmasSnowflakeShape.DOTTED:
        return getDottedFlake();
      case AiChatChristmasSnowflakeShape.BRANCHED:
        return getBranchedFlake();
      default:
        return assertNever(shape);
    }
  };

  const getDottedFlake = (): ReactElement => {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M12 2v20M2 12h20M5.5 5.5l13 13M18.5 5.5l-13 13" />
        <g fill="currentColor" stroke="none">
          <circle cx="12" cy="2" r="1" />
          <circle cx="12" cy="22" r="1" />
          <circle cx="2" cy="12" r="1" />
          <circle cx="22" cy="12" r="1" />
        </g>
      </svg>
    );
  };

  const getBranchedFlake = (): ReactElement => {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M12 3v18M3 12h18M6.5 6.5l11 11M17.5 6.5l-11 11M9 6l2 2M15 6l-2 2M9 18l2-2M15 18l-2-2M6 9l2 2M6 15l2-2M18 9l-2 2M18 15l-2-2" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    );
  };

  return (
    <div className={styles.snowfall} aria-hidden="true">
      {AI_CHAT_CHRISTMAS_SNOWFLAKES.map(getFlakeColumn)}
    </div>
  );
}
